import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  TextField,
  InputAdornment,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Link,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Language, getTranslations } from "util/i18n";

// Icons
import DataObjectIcon from "@mui/icons-material/DataObject";
import DescriptionIcon from "@mui/icons-material/Description";
import PaymentIcon from "@mui/icons-material/Payment";
import DrawIcon from "@mui/icons-material/Draw";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SpeedIcon from "@mui/icons-material/Speed";
import SavingsIcon from "@mui/icons-material/Savings";
import SecurityIcon from "@mui/icons-material/Security";
import CodeOffIcon from "@mui/icons-material/CodeOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import EmailIcon from "@mui/icons-material/Email";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const scaleIn = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } },
};

export default function LandingPageEnhanced() {
  useNavigate();
  const [language, setLanguage] = useState<Language>('lt');
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("monthly");
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    employees: "",
  });

  const t = getTranslations(language);

  const handleLanguageChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLanguage: Language | null,
  ) => {
    if (newLanguage !== null) {
      setLanguage(newLanguage);
    }
  };

  const handleOpenContactModal = () => {
    setContactModalOpen(true);
    setFormSubmitted(false);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const [, setIsSubmitting] = useState(false);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://formspree.io/f/xrbjnlwg", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          employees: formData.employees,
        }),
      });

      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => {
          setFormData({ name: "", email: "", company: "", employees: "" });
          setFormSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const employeeOptions = [
    { value: "1-10", label: t.employeeOptions1 },
    { value: "11-50", label: t.employeeOptions2 },
    { value: "51-200", label: t.employeeOptions3 },
    { value: "201-500", label: t.employeeOptions4 },
    { value: "500+", label: t.employeeOptions5 },
  ];

  const features = [
    {
      icon: <DataObjectIcon sx={{ fontSize: 32 }} />,
      title: t.dataInputTitle,
      description: t.dataInputDesc,
      color: "#6366F1",
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
      title: t.documentGenTitle,
      description: t.documentGenDesc,
      color: "#10B981",
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 32 }} />,
      title: t.paymentProcessingTitle,
      description: t.paymentProcessingDesc,
      color: "#F59E0B",
    },
    {
      icon: <DrawIcon sx={{ fontSize: 32 }} />,
      title: t.eSignatureTitle,
      description: t.eSignatureDesc,
      color: "#EC4899",
    },
    {
      icon: <NotificationsActiveIcon sx={{ fontSize: 32 }} />,
      title: t.notificationsTitle,
      description: t.notificationsDesc,
      color: "#8B5CF6",
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 32 }} />,
      title: t.identityVerificationTitle,
      description: t.identityVerificationDesc,
      color: "#06B6D4",
    },
  ];

  const benefits = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: t.fastImplementation,
      description: t.fastImplementationDesc,
      gradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    },
    {
      icon: <SavingsIcon sx={{ fontSize: 40 }} />,
      title: t.costEffective,
      description: t.costEffectiveDesc,
      gradient: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: t.secureCompliant,
      description: t.secureCompliantDesc,
      gradient: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
    },
    {
      icon: <CodeOffIcon sx={{ fontSize: 40 }} />,
      title: t.noCodeRequired,
      description: t.noCodeRequiredDesc,
      gradient: "linear-gradient(135deg, #EC4899 0%, #F472B6 100%)",
    },
  ];

  const stats = [
    { value: t.statROI, label: t.statROIDesc, icon: <TrendingUpIcon sx={{ fontSize: 40 }} /> },
    { value: t.statTimeSaved, label: t.statTimeSavedDesc, icon: <AccessTimeIcon sx={{ fontSize: 40 }} /> },
    { value: t.statCostSaved, label: t.statCostSavedDesc, icon: <SavingsIcon sx={{ fontSize: 40 }} /> },
  ].filter(stat => stat.value);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const testimonials = [
    {
      quote: t.testimonial1Quote,
      author: t.testimonial1Author,
      role: t.testimonial1Role,
      avatar: "J",
    },
    {
      quote: t.testimonial2Quote,
      author: t.testimonial2Author,
      role: t.testimonial2Role,
      avatar: "M",
    },
    {
      quote: t.testimonial3Quote,
      author: t.testimonial3Author,
      role: t.testimonial3Role,
      avatar: "T",
    },
  ];

  const pricingPlans = [
    {
      name: t.starterPlan,
      price: t.starterPrice,
      description: t.starterDesc,
      features: [t.starterFeature1, t.starterFeature2, t.starterFeature3, t.starterFeature4],
      popular: false,
      buttonText: t.choosePlan,
      buttonVariant: "outlined" as const,
    },
    {
      name: t.proPlan,
      price: t.proPrice,
      description: t.proDesc,
      features: [t.proFeature1, t.proFeature2, t.proFeature3, t.proFeature4, t.proFeature5],
      popular: true,
      buttonText: t.choosePlan,
      buttonVariant: "contained" as const,
    },
    {
      name: t.enterprisePlan,
      price: t.enterprisePrice,
      description: t.enterpriseDesc,
      features: [t.enterpriseFeature1, t.enterpriseFeature2, t.enterpriseFeature3, t.enterpriseFeature4, t.enterpriseFeature5],
      popular: false,
      buttonText: t.contactSales,
      buttonVariant: "outlined" as const,
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFFFF", overflowX: "hidden" }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AutoGraphIcon sx={{ color: "white", fontSize: 24 }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                recodinAI
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <ToggleButtonGroup
                value={language}
                exclusive
                onChange={handleLanguageChange}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "1px solid rgba(99, 102, 241, 0.2)",
                    borderRadius: "8px !important",
                    px: 2,
                    py: 0.5,
                    "&.Mui-selected": {
                      bgcolor: "rgba(99, 102, 241, 0.1)",
                      color: "#6366F1",
                    },
                  },
                }}
              >
                <ToggleButton value="lt">LT</ToggleButton>
                <ToggleButton value="en">EN</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                  },
                }}
                onClick={handleOpenContactModal}
              >
                {t.getStarted}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 16 },
          background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: "absolute",
            top: -200,
            right: -200,
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -100,
            left: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
              >
                <motion.div variants={fadeInUp}>
                  <Chip
                    label={t.heroTag}
                    sx={{
                      mb: 3,
                      bgcolor: "rgba(99, 102, 241, 0.1)",
                      color: "#6366F1",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      py: 2.5,
                      px: 1,
                    }}
                  />
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                      fontWeight: 700,
                      lineHeight: 1.1,
                      mb: 3,
                      color: "#0F172A",
                    }}
                  >
                    {t.heroTitle}{" "}
                    <Box
                      component="span"
                      sx={{
                        background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {t.heroTitleHighlight}
                    </Box>
                  </Typography>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#64748B",
                      fontWeight: 400,
                      lineHeight: 1.6,
                      mb: 4,
                      fontSize: "1.125rem",
                    }}
                  >
                    {t.heroSubtitle}
                  </Typography>
                </motion.div>

                <motion.div variants={fadeInUp}>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 4 }}>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                        px: 4,
                        py: 1.5,
                        fontSize: "1rem",
                        "&:hover": {
                          background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                      onClick={handleOpenContactModal}
                    >
                      {t.startFreeTrial}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PlayArrowRoundedIcon />}
                      sx={{
                        borderColor: "#E2E8F0",
                        color: "#475569",
                        px: 4,
                        py: 1.5,
                        fontSize: "1rem",
                        "&:hover": {
                          borderColor: "#6366F1",
                          bgcolor: "rgba(99, 102, 241, 0.05)",
                        },
                      }}
                      onClick={() =>
                        document
                          .getElementById("features")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                    >
                      {t.learnMore}
                    </Button>
                  </Box>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#94A3B8", fontSize: "0.875rem" }}
                  >
                    {t.noCreditCard}
                  </Typography>
                </motion.div>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={scaleIn}
              >
                {/* Contact Form Card */}
                <Card
                  id="contact-form"
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
                    border: "1px solid rgba(99, 102, 241, 0.1)",
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ mb: 1, fontWeight: 600, color: "#0F172A" }}
                  >
                    {t.contactFormTitle}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 3, color: "#64748B" }}
                  >
                    {t.contactFormSubtitle}
                  </Typography>

                  {formSubmitted ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {t.formSuccess}
                    </Alert>
                  ) : (
                    <Box component="form" onSubmit={handleFormSubmit}>
                      <TextField
                        fullWidth
                        placeholder={t.formNamePlaceholder}
                        value={formData.name}
                        onChange={(e) => handleFormChange("name", e.target.value)}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ color: "#94A3B8" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                            bgcolor: "#F8FAFC",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6366F1",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6366F1",
                            },
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        type="email"
                        placeholder={t.formEmailPlaceholder}
                        value={formData.email}
                        onChange={(e) => handleFormChange("email", e.target.value)}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: "#94A3B8" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                            bgcolor: "#F8FAFC",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6366F1",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6366F1",
                            },
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        placeholder={t.formCompanyPlaceholder}
                        value={formData.company}
                        onChange={(e) => handleFormChange("company", e.target.value)}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon sx={{ color: "#94A3B8" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                            bgcolor: "#F8FAFC",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6366F1",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6366F1",
                            },
                          },
                        }}
                      />

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <Select
                          value={formData.employees}
                          onChange={(e) => handleFormChange("employees", e.target.value)}
                          displayEmpty
                          required
                          startAdornment={
                            <InputAdornment position="start">
                              <GroupsIcon sx={{ color: "#94A3B8" }} />
                            </InputAdornment>
                          }
                          sx={{
                            borderRadius: 3,
                            bgcolor: "#F8FAFC",
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6366F1",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6366F1",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {t.formEmployeesPlaceholder}
                          </MenuItem>
                          {employeeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        sx={{
                          background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                          py: 1.5,
                          mb: 2,
                          "&:hover": {
                            background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                          },
                        }}
                      >
                        {t.formSubmit}
                      </Button>
                    </Box>
                  )}

                  <Typography
                    variant="caption"
                    sx={{ color: "#94A3B8", display: "block", textAlign: "center" }}
                  >
                    {t.formDisclaimer}
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, bgcolor: "#0F172A" }}>
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Grid container spacing={4} justifyContent="center">
              {stats.map((stat, index) => (
                <Grid item xs={6} md={4} key={index}>
                  <motion.div variants={fadeInUp}>
                    <Box sx={{ textAlign: "center" }}>
                      <Box
                        sx={{
                          color: "#6366F1",
                          mb: 1,
                          opacity: 0.8,
                        }}
                      >
                        {stat.icon}
                      </Box>
                      <Typography
                        variant="h2"
                        sx={{
                          color: "white",
                          fontWeight: 700,
                          fontSize: { xs: "2rem", md: "2.5rem" },
                          mb: 0.5,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#94A3B8", fontSize: "0.875rem" }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 10, md: 16 }, bgcolor: "#FFFFFF" }}>
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: "#0F172A",
                }}
              >
                {t.whyChooseTitle}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#64748B",
                  fontWeight: 400,
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                {t.whyChooseSubtitle}
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <Grid container spacing={3}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div variants={fadeInUp}>
                    <Card
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 3,
                        border: "1px solid #F1F5F9",
                        boxShadow: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.1)",
                          borderColor: "transparent",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          background: benefit.gradient,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          mb: 2,
                        }}
                      >
                        {benefit.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ mb: 1, fontWeight: 600, color: "#0F172A" }}
                      >
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#64748B", lineHeight: 1.6 }}>
                        {benefit.description}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: { xs: 10, md: 16 }, bgcolor: "#F8FAFC" }}>
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: "#0F172A",
                }}
              >
                {t.featuresTitle}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#64748B",
                  fontWeight: 400,
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                {t.featuresSubtitle}
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div variants={fadeInUp}>
                    <Card
                      sx={{
                        p: 3,
                        height: "100%",
                        borderRadius: 3,
                        bgcolor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        boxShadow: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 24px -8px rgba(0, 0, 0, 0.1)",
                          borderColor: feature.color,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: `${feature.color}15`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: feature.color,
                          mb: 2,
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ mb: 1, fontWeight: 600, color: "#0F172A" }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#64748B", lineHeight: 1.6 }}>
                        {feature.description}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ py: { xs: 10, md: 16 }, bgcolor: "#FFFFFF" }}>
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: "#0F172A",
                }}
              >
                {t.howItWorksTitle}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#64748B",
                  fontWeight: 400,
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                {t.howItWorksSubtitle}
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Grid container spacing={4}>
              {[
                { title: t.step1Title, desc: t.step1Desc },
                { title: t.step2Title, desc: t.step2Desc },
                { title: t.step3Title, desc: t.step3Desc },
                { title: t.step4Title, desc: t.step4Desc },
              ].map((step, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div variants={fadeInUp}>
                    <Box sx={{ textAlign: "center", position: "relative" }}>
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "1.75rem",
                          fontWeight: 700,
                          margin: "0 auto 1.5rem",
                          boxShadow: "0 8px 24px -8px rgba(99, 102, 241, 0.5)",
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{ mb: 1, fontWeight: 600, color: "#0F172A" }}
                      >
                        {step.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#64748B", lineHeight: 1.6 }}>
                        {step.desc}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Founder's Message */}
      <Box sx={{ py: { xs: 10, md: 16 }, bgcolor: "#F8FAFC" }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: "#0F172A",
                }}
              >
                {t.founderTitle}
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={scaleIn}
          >
            <Card
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 4,
                bgcolor: "#FFFFFF",
                border: "1px solid rgba(99, 102, 241, 0.2)",
                boxShadow: "0 20px 40px -12px rgba(99, 102, 241, 0.15)",
                textAlign: "center",
              }}
            >
              <Box
                component="img"
                src={require("assets/4F0A4096.jpg")}
                alt="Rokas Ulickas"
                sx={{
                  display: "block",
                  width: "auto",
                  height: 400,
                  maxWidth: "100%",
                  mx: "auto",
                  mb: 3,
                  borderRadius: 4,
                  border: "4px solid rgba(99, 102, 241, 0.2)",
                  boxShadow: "0 8px 24px -8px rgba(99, 102, 241, 0.3)",
                  objectFit: "cover",
                }}
              />
              <Typography
                variant="body"
                sx={{
                  color: "#475569",
                  lineHeight: 1.8,
                  mb: 4,
                  fontStyle: "italic",
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                "{t.founderQuote}"
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: "#0F172A", mb: 0.5 }}
              >
                {t.founderName}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6366F1", fontWeight: 500 }}>
                {t.founderRole}
              </Typography>
            </Card>
          </motion.div>
        </Container>
      </Box>

      {/* Pricing */}
      <Box id="pricing" sx={{ py: { xs: 10, md: 16 }, bgcolor: "#FFFFFF" }}>
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: "#0F172A",
                }}
              >
                {t.pricingTitle}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#64748B",
                  fontWeight: 400,
                  maxWidth: 600,
                  mx: "auto",
                  mb: 4,
                }}
              >
                {t.pricingSubtitle}
              </Typography>

              <ToggleButtonGroup
                value={billingPeriod}
                exclusive
                onChange={(_, value) => value && setBillingPeriod(value)}
                sx={{
                  bgcolor: "#F1F5F9",
                  borderRadius: 3,
                  p: 0.5,
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: "10px !important",
                    px: 3,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 500,
                    "&.Mui-selected": {
                      bgcolor: "#FFFFFF",
                      color: "#0F172A",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    },
                  },
                }}
              >
                <ToggleButton value="monthly">{t.pricingMonthly}</ToggleButton>
                <ToggleButton value="yearly">
                  {t.pricingYearly}
                  <Chip
                    label={t.pricingSave}
                    size="small"
                    sx={{
                      ml: 1,
                      bgcolor: "#10B981",
                      color: "white",
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <Grid container spacing={3} justifyContent="center">
              {pricingPlans.map((plan, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div variants={fadeInUp}>
                    <Card
                      sx={{
                        p: 4,
                        height: "100%",
                        borderRadius: 3,
                        border: plan.popular
                          ? "2px solid #6366F1"
                          : "1px solid #E2E8F0",
                        boxShadow: plan.popular
                          ? "0 20px 40px -12px rgba(99, 102, 241, 0.25)"
                          : "none",
                        position: "relative",
                        overflow: "visible",
                      }}
                    >
                      {plan.popular && (
                        <Chip
                          label={t.mostPopular}
                          sx={{
                            position: "absolute",
                            top: -12,
                            left: "50%",
                            transform: "translateX(-50%)",
                            bgcolor: "#6366F1",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      )}

                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, color: "#0F172A", mb: 1 }}
                      >
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#64748B", mb: 3 }}>
                        {plan.description}
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "baseline", mb: 3 }}>
                        {plan.price !== t.enterprisePrice ? (
                          <>
                            <Typography
                              variant="h2"
                              sx={{
                                fontWeight: 700,
                                color: "#0F172A",
                                fontSize: "3rem",
                              }}
                            >
                              {billingPeriod === "yearly"
                                ? Math.round(parseInt(plan.price) * 0.8)
                                : plan.price}
                              <Typography
                                component="span"
                                sx={{ fontSize: "1.5rem", color: "#64748B", ml: 0.5 }}
                              >
                                EUR
                              </Typography>
                            </Typography>
                            <Typography sx={{ color: "#64748B", ml: 1 }}>
                              {t.perMonth}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 600, color: "#0F172A" }}
                          >
                            {plan.price}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        {plan.features.map((feature, fIndex) => (
                          <Box
                            key={fIndex}
                            sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
                          >
                            <CheckCircleIcon
                              sx={{ color: "#10B981", mr: 1.5, fontSize: 20 }}
                            />
                            <Typography variant="body2" sx={{ color: "#475569" }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Button
                        fullWidth
                        variant={plan.buttonVariant}
                        size="large"
                        sx={{
                          py: 1.5,
                          ...(plan.popular && {
                            background:
                              "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                            },
                          }),
                          ...(!plan.popular && {
                            borderColor: "#E2E8F0",
                            color: "#475569",
                            "&:hover": {
                              borderColor: "#6366F1",
                              bgcolor: "rgba(99, 102, 241, 0.05)",
                            },
                          }),
                        }}
                        onClick={handleOpenContactModal}
                      >
                        {plan.buttonText}
                      </Button>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            height: 800,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={fadeInUp}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h2"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 3,
                  fontSize: { xs: "2rem", md: "2.75rem" },
                }}
              >
                {t.ctaTitle}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#94A3B8",
                  fontWeight: 400,
                  mb: 5,
                  maxWidth: 500,
                  mx: "auto",
                }}
              >
                {t.ctaSubtitle}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                    px: 5,
                    py: 2,
                    fontSize: "1.1rem",
                    "&:hover": {
                      background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                  onClick={handleOpenContactModal}
                >
                  {t.startYourFreeTrial}
                </Button>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "#64748B", mt: 3 }}
              >
                {t.noCreditCard}
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* EU Funding Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#F8FAFC" }}>
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <Card
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                bgcolor: "#FFFFFF",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: "#003399",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ color: "#FFCC00", fontSize: "1.5rem" }}>★</Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: "#0F172A" }}
                    >
                      {t.euFundingTitle}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#0F172A", mb: 2 }}
                  >
                    {t.euFundingProjectName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748B", lineHeight: 1.7, mb: 3 }}
                  >
                    {t.euFundingDescription}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#003399", fontWeight: 600 }}
                  >
                    {t.euFundingDisclaimer}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ p: 2, bgcolor: "#F1F5F9", borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ color: "#64748B", mb: 0.5 }}>
                        {t.euFundingPeriod}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#0F172A" }}>
                        {t.euFundingPeriodValue}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: "#F1F5F9", borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ color: "#64748B", mb: 0.5 }}>
                        {t.euFundingTotal}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#0F172A" }}>
                        {t.euFundingTotalValue}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: "#E8F4FD", borderRadius: 2, border: "1px solid #003399" }}>
                      <Typography variant="body2" sx={{ color: "#003399", mb: 0.5 }}>
                        {t.euFundingEU}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#003399" }}>
                        {t.euFundingEUValue}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* EU Funding Logos */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: { xs: 4, md: 6 },
                  mt: 4,
                  pt: 4,
                  borderTop: "1px solid #E2E8F0",
                }}
              >
                <Box
                  component="img"
                  src="/LT_00_Fichier de reproduction Next Generation EU_POS_POS.png"
                  alt="Finansuoja Europos Sąjunga"
                  sx={{ height: { xs: 55, md: 70 }, objectFit: "contain" }}
                />
                <Box
                  component="img"
                  src="/NKL-Logo_Horizontal-RGB.png"
                  alt="New Generation Lithuania"
                  sx={{ height: { xs: 55, md: 70 }, objectFit: "contain" }}
                />
              </Box>
            </Card>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 8, bgcolor: "#0F172A", color: "white" }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: { xs: 4, md: 0 }
          }}>
            {/* Left - Logo and info */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AutoGraphIcon sx={{ color: "white", fontSize: 24 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "white" }}>
                  recodinAI
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "#94A3B8", mb: 1.5, lineHeight: 1.7, maxWidth: 300 }}
              >
                {t.footerTagline}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748B", display: "flex", alignItems: "center", gap: 1 }}>
                {t.madeWithLove} <span role="img" aria-label="Lithuanian flag">🇱🇹</span>
              </Typography>
            </Box>

            {/* Center - Navigation links */}
            <Box sx={{ display: "flex", gap: 4 }}>
              <Link href="#features" sx={{ color: "#94A3B8", textDecoration: "none", "&:hover": { color: "white" } }}>
                {t.features}
              </Link>
              <Link href="#pricing" sx={{ color: "#94A3B8", textDecoration: "none", "&:hover": { color: "white" } }}>
                {t.pricing}
              </Link>
                     <Link
                            component="button"
                            onClick={handleOpenContactModal}
                            sx={{ color: "#94A3B8", textDecoration: "none", "&:hover": { color: "white" }, background: "none", border: "none", cursor: "pointer", p: 0, font: "inherit", fontSize: "0.875rem" }}
                          >
                            {t.contactUs}
                          </Link>
            </Box>

            {/* Right - Contact */}

          </Box>

          <Box
            sx={{
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              mt: 6,
              pt: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: "#64748B" }}>
              © {new Date().getFullYear()} recodinAI. {t.allRightsReserved}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Modal */}
      <Dialog
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 2,
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#0F172A" }}>
              {t.contactFormTitle}
            </Typography>
            <IconButton onClick={handleCloseContactModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ mb: 3, color: "#64748B" }}>
            {t.contactFormSubtitle}
          </Typography>

          {formSubmitted ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t.formSuccess}
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleFormSubmit}>
              <TextField
                fullWidth
                label={t.formName}
                placeholder={t.formNamePlaceholder}
                value={formData.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                type="email"
                label={t.formEmail}
                placeholder={t.formEmailPlaceholder}
                value={formData.email}
                onChange={(e) => handleFormChange("email", e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                fullWidth
                label={t.formCompany}
                placeholder={t.formCompanyPlaceholder}
                value={formData.company}
                onChange={(e) => handleFormChange("company", e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon sx={{ color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>{t.formEmployees}</InputLabel>
                <Select
                  value={formData.employees}
                  onChange={(e) => handleFormChange("employees", e.target.value)}
                  label={t.formEmployees}
                  required
                  startAdornment={
                    <InputAdornment position="start">
                      <GroupsIcon sx={{ color: "#94A3B8" }} />
                    </InputAdornment>
                  }
                  sx={{ borderRadius: 2 }}
                >
                  {employeeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                  py: 1.5,
                  "&:hover": {
                    background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                  },
                }}
              >
                {t.formSubmit}
              </Button>
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{ color: "#94A3B8", display: "block", textAlign: "center", mt: 2 }}
          >
            {t.formDisclaimer}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
